import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';
import { DataView } from 'primereact/dataview';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { clientsApi, quickbooksApi } from '../api';
import { Client } from '../types';

interface QBInfoItem {
  key: string;
  label: string;
  value: string;
}

interface ClientSettingsProps {
  clientId: string;
  visible: boolean;
  onHide: () => void;
  onClientUpdated?: (client: Client) => void;
}

export const ClientSettings: React.FC<ClientSettingsProps> = ({
  clientId,
  visible,
  onHide,
  onClientUpdated,
}) => {
  const toast = useRef<Toast>(null);

  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnectingQB, setIsConnectingQB] = useState(false);
  const [isResettingQB, setIsResettingQB] = useState(false);
  const [isDisconnectingQB, setIsDisconnectingQB] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowCorporateCustomers, setAllowCorporateCustomers] = useState(true);
  const [developerMode, setDeveloperMode] = useState(false);

  // Original values for change detection
  const [originalValues, setOriginalValues] = useState<{
    name: string;
    description: string;
    allowCorporateCustomers: boolean;
    developerMode: boolean;
  } | null>(null);

  // Realm mismatch state
  const [realmMismatchError, setRealmMismatchError] = useState<{
    message: string;
    canReset: boolean;
  } | null>(null);

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (clientId && visible) {
      loadClient();
    }
  }, [clientId, visible]);

  const loadClient = async (): Promise<Client | null> => {
    if (!clientId) return null;

    try {
      setIsLoading(true);
      setError(null);
      setRealmMismatchError(null);
      const data = await clientsApi.getClient(clientId);
      setClient(data);

      const values = {
        name: data.name,
        description: data.description || '',
        allowCorporateCustomers: data.allow_corporate_customers ?? true,
        developerMode: false, // TODO: Load from user preferences
      };

      setName(values.name);
      setDescription(values.description);
      setAllowCorporateCustomers(values.allowCorporateCustomers);
      setDeveloperMode(values.developerMode);
      setOriginalValues(values);
      return data;
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to load client');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle postMessage from OAuth popup
  const handleOAuthMessage = useCallback(async (event: MessageEvent) => {
    // Accept messages from backend (OAuth callback) or same origin
    const allowedOrigins = [window.location.origin, 'http://localhost:8001'];
    if (!allowedOrigins.includes(event.origin)) return;

    const data = event.data;
    if (data?.type !== 'qb_oauth_complete') return;

    setIsConnectingQB(false);

    if (data.success) {
      // Call complete endpoint to verify and get updated client data
      try {
        const result = await quickbooksApi.completeLink(clientId);
        if (result.success) {
          setSuccess(`Connected to QuickBooks: ${result.company_name || 'Unknown Company'}`);
          // Fetch fresh client data directly to avoid stale closure issues
          const freshClient = await clientsApi.getClient(clientId);
          setClient(freshClient);
          onClientUpdated?.(freshClient);
        } else if (result.error_code === 'REALM_MISMATCH') {
          setRealmMismatchError({
            message: `This client is already configured for "${result.existing_company_name}", but you're trying to connect to "${result.new_company_name}". If this is intentional, you must first reset this client.`,
            canReset: result.can_reset || false,
          });
        } else {
          setError(result.message || 'Failed to complete QuickBooks connection');
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to complete QuickBooks connection');
      }
    } else {
      setError(data.error || 'QuickBooks authorization was not completed');
    }
  }, [clientId, onClientUpdated]);

  // Set up message listener for OAuth callback
  useEffect(() => {
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [handleOAuthMessage]);

  const handleConnectQuickBooks = async () => {
    setIsConnectingQB(true);
    setError(null);
    setSuccess(null);
    setRealmMismatchError(null);

    try {
      const { authorization_url } = await quickbooksApi.startLink(clientId);

      // Open popup window for OAuth flow
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authorization_url,
        'QuickBooks Authorization',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      // Check if popup was blocked
      if (!popup) {
        setError('Popup was blocked. Please allow popups for this site and try again.');
        setIsConnectingQB(false);
        return;
      }

      // Monitor popup close (in case user closes without completing)
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          // Give time for message to be processed
          setTimeout(() => {
            setIsConnectingQB(false);
          }, 500);
        }
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start QuickBooks connection');
      setIsConnectingQB(false);
    }
  };

  const performDisconnect = async () => {
    setIsDisconnectingQB(true);
    setError(null);
    setSuccess(null);

    try {
      await quickbooksApi.disconnect(clientId);
      setSuccess('QuickBooks disconnected successfully');
      const updatedClient = await loadClient();
      if (updatedClient) {
        onClientUpdated?.(updatedClient);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to disconnect QuickBooks');
    } finally {
      setIsDisconnectingQB(false);
    }
  };

  const handleDisconnectQuickBooks = () => {
    setShowDisconnectConfirm(true);
  };

  const confirmDisconnect = () => {
    setShowDisconnectConfirm(false);
    performDisconnect();
  };

  const cancelDisconnect = () => {
    setShowDisconnectConfirm(false);
  };

  const handleResetQuickBooks = async () => {
    setIsResettingQB(true);
    setError(null);
    setRealmMismatchError(null);

    try {
      await quickbooksApi.resetLink(clientId);
      setSuccess('QuickBooks connection reset. You can now connect to a different company.');
      await loadClient();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset QuickBooks connection');
    } finally {
      setIsResettingQB(false);
    }
  };

  const handleSave = async () => {
    if (!clientId) return;
    if (!name.trim()) {
      setError('Client name is required');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await clientsApi.updateClient(clientId, {
        name: name.trim(),
        description: description.trim() || undefined,
        allow_corporate_customers: allowCorporateCustomers,
      });

      setClient(updated);

      const values = {
        name: updated.name,
        description: updated.description || '',
        allowCorporateCustomers: updated.allow_corporate_customers ?? true,
        developerMode,
      };

      setOriginalValues(values);
      setSuccess('Settings saved successfully');

      onClientUpdated?.(updated);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const formatAddress = (addrJson: string | null): string => {
    if (!addrJson) return 'N/A';
    try {
      const addr = JSON.parse(addrJson);
      const parts = [];
      if (addr.Line1 || addr.line1) parts.push(addr.Line1 || addr.line1);
      if (addr.Line2 || addr.line2) parts.push(addr.Line2 || addr.line2);
      if (addr.City || addr.city) parts.push(addr.City || addr.city);
      if (addr.CountrySubDivisionCode || addr.state) parts.push(addr.CountrySubDivisionCode || addr.state);
      if (addr.PostalCode || addr.postalCode || addr.zip) parts.push(addr.PostalCode || addr.postalCode || addr.zip);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const formatTokenExpiry = (expiryStr: string | null): string => {
    if (!expiryStr) return 'N/A';
    try {
      return new Date(expiryStr).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const hasChanges = originalValues && (
    name !== originalValues.name ||
    description !== originalValues.description ||
    allowCorporateCustomers !== originalValues.allowCorporateCustomers ||
    developerMode !== originalValues.developerMode
  );

  const isQBConnected = !!client?.qbo_realm_id;

  // Prepare QuickBooks info for DataView
  const qbInfoItems: QBInfoItem[] = [
    {
      key: 'realm_id',
      label: 'Realm ID',
      value: client?.qbo_realm_id || 'Not configured',
    },
    {
      key: 'legal_name',
      label: 'Legal Name',
      value: client?.qbo_legal_name || 'Not available',
    },
    {
      key: 'company_name',
      label: 'Company Name',
      value: client?.qbo_company_name || 'Not available',
    },
    {
      key: 'address',
      label: 'Company Address',
      value: formatAddress(client?.qbo_company_address || null),
    },
    {
      key: 'token_expires',
      label: 'Token Expires At',
      value: formatTokenExpiry(client?.qbo_token_expires_at || null),
    },
    {
      key: 'linked_at',
      label: 'Linked At',
      value: client?.qbo_linked_at ? new Date(client.qbo_linked_at).toLocaleString() : 'N/A',
    },
  ];

  const qbInfoTemplate = (item: QBInfoItem) => {
    return (
      <div className="qb-info-item">
        <span className="qb-info-label">{item.label}:</span>
        <span className="qb-info-value">{item.value}</span>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <i className="ms-out ms-out-progress-activity loading-spinner" />
          <p>Loading settings...</p>
        </div>
      );
    }

    return (
      <div className="project-settings">
        <div className="form-grid">
          <div className="form-field">
            <label htmlFor="client-name">Name *</label>
            <InputText
              id="client-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
              placeholder="Enter client name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="description">Description</label>
            <InputTextarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSaving}
              placeholder="Enter description"
              rows={2}
            />
          </div>

          <div className="form-field">
            <label>QuickBooks Connection</label>
            {isQBConnected ? (
              <div className="qb-connection-status">
                <div className="qb-connected-info">
                  <i className="ms-out ms-out-check-circle icon-success" />
                  <span>Connected to {client?.qbo_company_name || 'QuickBooks'}</span>
                </div>
                <Button
                  label="Disconnect"
                  icon="ms-out ms-out-link-off"
                  severity="warning"
                  outlined
                  size="small"
                  onClick={handleDisconnectQuickBooks}
                  loading={isDisconnectingQB}
                  disabled={isSaving || isConnectingQB}
                />
              </div>
            ) : (
              <div className="qb-connection-status">
                <Button
                  label="Connect to QuickBooks"
                  icon="ms-out ms-out-link"
                  onClick={handleConnectQuickBooks}
                  loading={isConnectingQB}
                  disabled={isSaving || isConnectingQB}
                />
              </div>
            )}
          </div>

          {realmMismatchError && (
            <div className="form-field">
              <Message severity="warn" text={realmMismatchError.message} />
              {realmMismatchError.canReset && (
                <Button
                  label="Reset QuickBooks Connection"
                  icon="ms-out ms-out-refresh"
                  severity="warning"
                  size="small"
                  onClick={handleResetQuickBooks}
                  loading={isResettingQB}
                  disabled={isSaving || isResettingQB}
                />
              )}
            </div>
          )}

          <Divider />
          <div className="form-field inline">
              <InputSwitch
                id="allow-corporate-customers"
                checked={allowCorporateCustomers}
                onChange={(e) => setAllowCorporateCustomers(e.value)}
                disabled={isSaving}
              />
            <label htmlFor="allow-corporate-customers">Allow Corporate Customers</label>
          </div>

          <div className="form-field inline">
            <InputSwitch
              id="developer-mode"
              checked={developerMode}
              onChange={(e) => setDeveloperMode(e.value)}
              disabled={isSaving}
            />
            <label htmlFor="developer-mode">Developer Mode</label>
          </div>
        </div>

        <DataView
          value={qbInfoItems}
          itemTemplate={qbInfoTemplate}
          layout="grid"
          className="qb-info-dataview"
          header="QuickBooks Info"
        />

        <div className="dialog-actions">
          <div className="messages">
            {error && <Message severity="error" text={error} className="settings-message" />}
            {success && <Message severity="success" text={success} className="settings-message" />}
          </div>
          <Button
            label="Save"
            icon="ms-out ms-out-save"
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaving || isConnectingQB || !hasChanges}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        visible={showDisconnectConfirm}
        onHide={cancelDisconnect}
        message="Are you sure you want to disconnect QuickBooks? This will clear the stored credentials."
        header="Disconnect QuickBooks"
        icon="pi pi-exclamation-triangle"
        acceptClassName="p-button-warning"
        accept={confirmDisconnect}
        reject={cancelDisconnect}
        appendTo={document.body}
        baseZIndex={2000}
      />
      <Dialog
        header="Client Settings"
        visible={visible}
        onHide={onHide}
        className="large-dialog"
        modal
        closable={!isSaving}
      >
        {renderContent()}
      </Dialog>
    </>
  );
};

export default ClientSettings;
